import React, { useEffect, useMemo, useState } from "react";
import useApi from "../../CustomHooks/useApi";
import { membershipAPI } from "../../../api/apiClient";
import Loading from "../../../Loading";
import MembershipDiscordSelector from "./MembershipDiscordSelector";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faPlus, faTimes, faSort, faSortUp, faSortDown, faUserMinus, faPen } from "@fortawesome/free-solid-svg-icons";
import { FaDiscord } from "react-icons/fa";
import "../../../css/Views/MembershipView.css";
import "../../../css/Views/MembershipDiscord.css";

const monthNames = ["Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec", "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"];
const monthNamesGenitive = ["stycznia", "lutego", "marca", "kwietnia", "maja", "czerwca", "lipca", "sierpnia", "września", "października", "listopada", "grudnia"];

const emptyForm = { firstName: "", lastName: "", email: "", joinedAt: "", membershipFeeStartsFromJoiningMonth: false, comment: "", discordUserId: null, discordDisplayName: null };

const getDiscordRoleStatus = (member) => {
    const status = member.discordRoleStatus;

    if (status === "HasRole" || status === 1 || status === "hasRole")
        return { label: "Rola osoby członkowskiej aktywna", shortLabel: "✓", className: "has-role" };

    if (status === "MissingRole" || status === 2 || status === "missingRole")
        return { label: "Brak roli osoby członkowskiej", shortLabel: "⚠️ Brak roli", className: "missing-role" };

    if (status === "NotOnServer" || status === 3 || status === "notOnServer")
        return { label: "Konto nie jest już na serwerze", shortLabel: "Poza serwerem", className: "not-on-server" };

    if (status === "DiscordUnavailable" || status === 4 || status === "unavailable")
        return { label: "Nie udało się zweryfikować Discorda", shortLabel: "Niedostępny", className: "unavailable" };

    if (!member.discordUserId)
        return { label: "Brak powiązanego konta Discord", shortLabel: "", className: "not-linked" };

    return { label: "Nie udało się zweryfikować roli", shortLabel: "?", className: "not-linked" };
};

const MembershipView = () => {
    const { callApi, isLoading, error } = useApi();
    const { callApi: callSaveApi, isLoading: isSaving } = useApi();
    const { callApi: callDetailsApi, isLoading: isLoadingDetails } = useApi();

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    const [year, setYear] = useState(currentYear);
    const [month, setMonth] = useState(currentMonth);
    const [members, setMembers] = useState([]);
    const [statusFilter, setStatusFilter] = useState("active");
    const [search, setSearch] = useState("");
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isResignModalOpen, setIsResignModalOpen] = useState(false);
    const [editingMember, setEditingMember] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [resignedAt, setResignedAt] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });

    const availableYears = Array.from({ length: currentYear - 2026 + 1 }, (_, index) => currentYear - index);

    const fetchMembers = () => {
        callApi(() => membershipAPI.getMembers(year, month), (data) => setMembers(Array.isArray(data) ? data : []));
    };

    useEffect(() => {
        fetchMembers();
    }, [year, month]);

    const formatDate = (date) => date ? new Date(date).toLocaleDateString("pl-PL") : "—";
    const formatInputDate = (date) => date ? date.substring(0, 10) : "";
    const formatAmount = (amount) => new Intl.NumberFormat("pl-PL", { style: "currency", currency: "PLN" }).format(Number(amount ?? 0));

    const activeMembersCount = useMemo(() => members.filter((member) => member.isActive).length, [members]);
    const membersWithDiscordCount = useMemo(() => members.filter((member) => member.isActive && member.discordUserId).length, [members]);
    const membersWithArrearsCount = useMemo(() => members.filter((member) => member.isActive && Number(member.arrears) > 0).length, [members]);
    const totalArrears = useMemo(() => members.reduce((sum, member) => sum + (member.isActive ? Number(member.arrears ?? 0) : 0), 0), [members]);

    const handleSort = (key) => {
        setSortConfig((current) => current.key === key ? { key, direction: current.direction === "asc" ? "desc" : "asc" } : { key, direction: "asc" });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return faSort;
        return sortConfig.direction === "asc" ? faSortUp : faSortDown;
    };

    const filteredMembers = useMemo(() => {
        const normalizedSearch = search.trim().toLocaleLowerCase("pl");

        const result = members.filter((member) => {
            if (statusFilter === "active" && !member.isActive) return false;
            if (statusFilter === "inactive" && member.isActive) return false;
            if (statusFilter === "arrears" && (!member.isActive || Number(member.arrears) <= 0)) return false;
            if (!normalizedSearch) return true;

            const text = `${member.firstName ?? ""} ${member.lastName ?? ""} ${member.email ?? ""} ${member.comment ?? ""} ${member.discordDisplayName ?? ""}`.toLocaleLowerCase("pl");
            return text.includes(normalizedSearch);
        });

        return result.sort((a, b) => {
            let aValue;
            let bValue;

            switch (sortConfig.key) {
                case "name":
                    aValue = `${a.firstName ?? ""} ${a.lastName ?? ""}`.toLocaleLowerCase("pl");
                    bValue = `${b.firstName ?? ""} ${b.lastName ?? ""}`.toLocaleLowerCase("pl");
                    break;
                case "joinedAt":
                    aValue = a.joinedAt ? new Date(a.joinedAt).getTime() : 0;
                    bValue = b.joinedAt ? new Date(b.joinedAt).getTime() : 0;
                    break;
                case "resignedAt":
                    aValue = a.resignedAt ? new Date(a.resignedAt).getTime() : 0;
                    bValue = b.resignedAt ? new Date(b.resignedAt).getTime() : 0;
                    break;
                case "status":
                    aValue = a.isActive ? 1 : 0;
                    bValue = b.isActive ? 1 : 0;
                    break;
                case "amountDue":
                    aValue = Number(a.amountDue ?? 0);
                    bValue = Number(b.amountDue ?? 0);
                    break;
                case "amountPaid":
                    aValue = Number(a.amountPaid ?? 0);
                    bValue = Number(b.amountPaid ?? 0);
                    break;
                case "arrears":
                    aValue = Number(a.arrears ?? 0);
                    bValue = Number(b.arrears ?? 0);
                    break;
                case "overpayment":
                    aValue = Number(a.overpayment ?? 0);
                    bValue = Number(b.overpayment ?? 0);
                    break;
                default:
                    aValue = `${a.firstName ?? ""} ${a.lastName ?? ""}`.toLocaleLowerCase("pl");
                    bValue = `${b.firstName ?? ""} ${b.lastName ?? ""}`.toLocaleLowerCase("pl");
                    break;
            }

            if (typeof aValue === "string" && typeof bValue === "string") {
                const comparison = aValue.localeCompare(bValue, "pl");
                return sortConfig.direction === "asc" ? comparison : -comparison;
            }

            if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
        });
    }, [members, statusFilter, search, sortConfig]);

    const handleAdd = () => {
        setEditingMember(null);
        setForm(emptyForm);
        setIsFormOpen(true);
    };

    const mapMemberToForm = (member) => ({
        firstName: member.firstName ?? "",
        lastName: member.lastName ?? "",
        email: member.email ?? "",
        joinedAt: formatInputDate(member.joinedAt),
        membershipFeeStartsFromJoiningMonth: Boolean(member.membershipFeeStartsFromJoiningMonth),
        comment: member.comment ?? "",
        discordUserId: member.discordUserId ?? null,
        discordDisplayName: member.discordDisplayName ?? null
    });

    const handleEdit = (member) => {
        setEditingMember(member);
        setForm(mapMemberToForm(member));
        setIsFormOpen(true);

        callDetailsApi(() => membershipAPI.getMember(member.guid, year, month), (details) => {
            setEditingMember(details);
            setForm(mapMemberToForm(details));
        });
    };

    const handleCloseForm = () => {
        setIsFormOpen(false);
        setEditingMember(null);
        setForm(emptyForm);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setForm((current) => ({ ...current, [name]: value }));
    };

    const handleDiscordChange = (discordUserId, discordDisplayName) => {
        setForm((current) => ({ ...current, discordUserId, discordDisplayName }));
    };

    const handleSave = (e) => {
        e.preventDefault();

        const payload = {
            firstName: form.firstName,
            lastName: form.lastName,
            email: form.email || null,
            joinedAt: form.joinedAt,
            membershipFeeStartsFromJoiningMonth: form.membershipFeeStartsFromJoiningMonth,
            comment: form.comment || null,
            discordUserId: form.discordUserId || null
        };

        if (!editingMember) {
            callSaveApi(() => membershipAPI.createMember(payload), () => {
                handleCloseForm();
                fetchMembers();
            });
            return;
        }

        callSaveApi(() => membershipAPI.updateMember(editingMember.guid, payload), () => {
            handleCloseForm();
            fetchMembers();
        });
    };

    const handleOpenResignModal = () => {
        setResignedAt(new Date().toISOString().substring(0, 10));
        setIsResignModalOpen(true);
    };

    const handleCloseResignModal = () => {
        setIsResignModalOpen(false);
        setResignedAt("");
    };

    const handleResign = () => {
        if (!editingMember?.isActive || !resignedAt) return;

        callSaveApi(() => membershipAPI.resignMember(editingMember.guid, resignedAt), () => {
            handleCloseResignModal();
            handleCloseForm();
            fetchMembers();
        });
    };

    return (
        <div className="membership-view">
            <div className="membership-view-header">Osoby członkowskie</div>

            <div className="membership-filters-panel">
                <div className="membership-filters-main">
                    <div className="membership-filter-field membership-filter-year"><span className="membership-filter-label">Rok</span><select value={year} onChange={(e) => setYear(Number(e.target.value))}>{availableYears.map((availableYear) => <option key={availableYear} value={availableYear}>{availableYear}</option>)}</select></div>
                    <div className="membership-filter-field membership-filter-month"><span className="membership-filter-label">Miesiąc</span><select value={month} onChange={(e) => setMonth(Number(e.target.value))}>{monthNames.map((monthName, index) => <option key={monthName} value={index + 1}>{monthName}</option>)}</select></div>
                    <div className="membership-filter-field membership-filter-status"><span className="membership-filter-label">Status</span><select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}><option value="all">Wszyscy</option><option value="active">Aktywni</option><option value="arrears">Z zaległością</option><option value="inactive">Byłe os. członk.</option></select></div>
                    <div className="membership-filter-field membership-filter-search"><span className="membership-filter-label">Szukaj</span><div className="membership-search-wrapper"><FontAwesomeIcon icon={faSearch} className="membership-search-icon" /><input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Imię, nazwisko, e-mail, Discord..." /></div></div>
                </div>
                <div className="membership-filters-actions"><button className="membership-add-action" onClick={handleAdd}><FontAwesomeIcon icon={faPlus} /> Dodaj os. członkowską</button></div>
            </div>

            {statusFilter !== "inactive" && <div className="membership-summary"><div className="membership-summary-card"><span>Aktywne os. członk.</span><strong>{activeMembersCount}</strong></div><div className="membership-summary-card"><span>Powiązany Discord</span><strong>{membersWithDiscordCount}</strong></div><div className={`membership-summary-card ${membersWithArrearsCount > 0 ? "membership-summary-warning" : ""}`}><span>Z zaległością do {monthNamesGenitive[month - 1]}</span><strong>{membersWithArrearsCount}</strong></div><div className={`membership-summary-card ${totalArrears > 0 ? "membership-summary-warning" : ""}`}><span>Łączna zaległość</span><strong>{formatAmount(totalArrears)}</strong></div></div>}

            {isLoading && <Loading text="Ładowanie osób członkowskich..." />}
            {error && <p className="error">Błąd: {error.message}</p>}
            {!isLoading && filteredMembers.length === 0 && <div className="membership-empty">Brak os. członkowskich dla wybranych filtrów.</div>}

            {!isLoading && filteredMembers.length > 0 && (
                <div className={`membership-table-wrapper ${statusFilter === "inactive" ? "membership-table-wrapper-inactive" : ""}`}>
                    <table className={`membership-table ${statusFilter === "inactive" ? "membership-table-inactive" : ""}`}>
                        <thead><tr><th className="membership-lp-column">Lp.</th><th onClick={() => handleSort("name")} className="membership-sortable"><span>Osoba</span><FontAwesomeIcon icon={getSortIcon("name")} /></th><th onClick={() => handleSort("joinedAt")} className="membership-sortable"><span>Członkostwo od</span><FontAwesomeIcon icon={getSortIcon("joinedAt")} /></th>{statusFilter === "inactive" ? <th onClick={() => handleSort("resignedAt")} className="membership-sortable"><span>Członkostwo do</span><FontAwesomeIcon icon={getSortIcon("resignedAt")} /></th> : <><th onClick={() => handleSort("status")} className="membership-sortable"><span>Status</span><FontAwesomeIcon icon={getSortIcon("status")} /></th><th onClick={() => handleSort("amountDue")} className="membership-sortable"><span>Należne do {monthNamesGenitive[month - 1]}</span><FontAwesomeIcon icon={getSortIcon("amountDue")} /></th><th onClick={() => handleSort("amountPaid")} className="membership-sortable"><span>Wpłacono</span><FontAwesomeIcon icon={getSortIcon("amountPaid")} /></th><th onClick={() => handleSort("arrears")} className="membership-sortable"><span>Zaległość</span><FontAwesomeIcon icon={getSortIcon("arrears")} /></th><th onClick={() => handleSort("overpayment")} className="membership-sortable"><span>Nadpłata</span><FontAwesomeIcon icon={getSortIcon("overpayment")} /></th></>}<th>Komentarz</th><th></th></tr></thead>
                        <tbody>
                            {filteredMembers.map((member, index) => {
                                const discordStatus = getDiscordRoleStatus(member);
                                return (
                                    <tr key={member.guid} className={`membership-table-row ${!member.isActive ? "membership-table-row-inactive" : ""}`}>
                                        <td className="membership-lp-column">{index + 1}</td>
                                        <td className="membership-person-cell">
                                            <span className="membership-person-name">{member.firstName} {member.lastName}</span>
                                            <span className={`membership-person-discord membership-person-discord-${discordStatus.className}`} title={discordStatus.label}>
                                                <FaDiscord className="membership-person-discord-icon" />
                                                <span className="membership-person-discord-name">{member.discordDisplayName || "Niepowiązany"}</span>
                                                {discordStatus.shortLabel && <span className="membership-person-discord-state">{discordStatus.shortLabel}</span>}
                                            </span>
                                        </td>
                                        <td className="membership-date-cell">{formatDate(member.joinedAt)}</td>
                                        {statusFilter === "inactive" ? <td className="membership-date-cell">{formatDate(member.resignedAt)}</td> : <><td>{member.isActive ? <span className="membership-status membership-status-active">Aktywny</span> : <div className="membership-status-wrapper"><span className="membership-status membership-status-inactive">Były</span><small>{formatDate(member.resignedAt)}</small></div>}</td><td className="membership-amount">{member.isActive ? formatAmount(member.amountDue) : "—"}</td><td className={`membership-amount ${member.isActive ? "membership-paid" : ""}`}>{member.isActive ? formatAmount(member.amountPaid) : "—"}</td><td className={`membership-amount ${member.isActive && Number(member.arrears) > 0 ? "membership-arrears" : ""}`}>{member.isActive ? formatAmount(member.arrears) : "—"}</td><td className={`membership-amount ${member.isActive && Number(member.overpayment) > 0 ? "membership-overpayment" : ""}`}>{member.isActive && Number(member.overpayment) > 0 ? formatAmount(member.overpayment) : "—"}</td></>}
                                        <td className="membership-comment-cell"><span title={member.comment ?? ""}>{member.comment || "—"}</span></td>
                                        <td className="membership-actions-cell"><button type="button" className="membership-row-action" onClick={() => handleEdit(member)} title="Edytuj"><FontAwesomeIcon icon={faPen} /></button></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {isFormOpen && (
                <div className="membership-modal-backdrop" onMouseDown={handleCloseForm}>
                    <div className="membership-modal" onMouseDown={(e) => e.stopPropagation()}>
                        <div className="membership-modal-header"><h3>{editingMember ? "Edycja osoby" : "Nowa os. członk."}</h3><button type="button" className="membership-modal-close" onClick={handleCloseForm}><FontAwesomeIcon icon={faTimes} /></button></div>
                        {isLoadingDetails ? <div className="membership-modal-loading"><Loading text="Ładowanie szczegółów osoby..." /></div> : (
                            <form onSubmit={handleSave}>
                                <div className="membership-modal-section">
                                    <div className="membership-modal-section-title">Dane osoby</div>
                                    <div className="membership-form-grid">
                                        <label><span>Imię</span><input type="text" name="firstName" value={form.firstName} onChange={handleFormChange} required /></label>
                                        <label><span>Nazwisko</span><input type="text" name="lastName" value={form.lastName} onChange={handleFormChange} required /></label>
                                        <label><span>E-mail</span><input type="email" name="email" value={form.email} onChange={handleFormChange} /></label>
                                        <label><span>Członkostwo od</span><input type="date" name="joinedAt" value={form.joinedAt} onChange={handleFormChange} required /></label>
                                        <label><span>Naliczanie składki od</span><select value={form.membershipFeeStartsFromJoiningMonth ? "joining" : "next"} onChange={(e) => setForm((current) => ({ ...current, membershipFeeStartsFromJoiningMonth: e.target.value === "joining" }))}><option value="joining">miesiąca przyjęcia</option><option value="next">kolejnego miesiąca</option></select></label>
                                        <div className="membership-form-discord"><span>Discord</span><MembershipDiscordSelector value={form.discordUserId} displayName={form.discordDisplayName} onChange={handleDiscordChange} /></div>
                                        <label className="membership-form-comment"><span>Komentarz</span><textarea name="comment" value={form.comment} onChange={handleFormChange} rows="4" /></label>
                                    </div>
                                </div>
                                {editingMember && <div className="membership-modal-section"><div className="membership-membership-info"><div className="membership-finance-summary"><div className="membership-finance-summary-row"><span>Status</span>{editingMember.isActive ? <strong className="membership-membership-active">Aktywny</strong> : <strong className="membership-membership-inactive">Była os. członk.</strong>}</div>{!editingMember.isActive && <div className="membership-finance-summary-row"><span>Członkostwo do</span><strong>{formatDate(editingMember.resignedAt)}</strong></div>}{editingMember.isActive && <><div className="membership-finance-summary-row"><span>Wpłacono w {year}</span><strong className="membership-paid">{formatAmount(editingMember.amountPaid)}</strong></div>{Number(editingMember.arrears) > 0 ? <div className="membership-finance-summary-row"><span>Zaległość na {monthNamesGenitive[month - 1]}</span><strong className="membership-finance-arrears">{formatAmount(editingMember.arrears)}</strong></div> : Number(editingMember.overpayment) > 0 ? <div className="membership-finance-summary-row"><span>Nadpłata</span><strong className="membership-overpayment">{formatAmount(editingMember.overpayment)}</strong></div> : <div className="membership-finance-summary-row"><span>Stan składek</span><strong className="membership-paid">Rozliczone</strong></div>}</>}</div><div className="membership-payments"><div className="membership-payments-header"><span>Wpłaty składek w {year}</span><strong>{editingMember.payments?.length ?? 0}</strong></div>{editingMember.payments?.length > 0 ? <div className="membership-payments-list">{editingMember.payments.map((payment) => <div key={payment.guid} className="membership-payment-row"><div className="membership-payment-info"><span className="membership-payment-date">{formatDate(payment.transferDate)}</span>{payment.title && <span className="membership-payment-title" title={payment.title}>{payment.title}</span>}</div><strong className="membership-payment-amount">{formatAmount(payment.amount)}</strong></div>)}</div> : <div className="membership-payments-empty">Brak przypisanych wpłat w {year} roku.</div>}</div></div></div>}
                                <div className="membership-form-actions"><div>{editingMember?.isActive && <button type="button" className="membership-resign-action" onClick={handleOpenResignModal} disabled={isSaving}><FontAwesomeIcon icon={faUserMinus} /> Zakończ członkostwo</button>}</div><div className="membership-form-actions-right"><button type="button" className="membership-cancel-action" onClick={handleCloseForm}>Anuluj</button><button type="submit" className="membership-save-action" disabled={isSaving}>{isSaving ? "Zapisywanie..." : "Zapisz"}</button></div></div>
                            </form>
                        )}
                    </div>
                </div>
            )}

            {isResignModalOpen && <div className="membership-modal-backdrop" onMouseDown={handleCloseResignModal}><div className="membership-resign-modal" onMouseDown={(e) => e.stopPropagation()}><div className="membership-modal-header"><h3>Zakończenie członkostwa</h3><button type="button" className="membership-modal-close" onClick={handleCloseResignModal}><FontAwesomeIcon icon={faTimes} /></button></div><div className="membership-resign-modal-content"><p>Czy chcesz zakończyć członkostwo osoby <strong>{editingMember?.firstName} {editingMember?.lastName}</strong>? Jeśli konto Discord jest powiązane, rola osoby członkowskiej zostanie odebrana automatycznie.</p><label><span>Data zakończenia członkostwa</span><input type="date" value={resignedAt} onChange={(e) => setResignedAt(e.target.value)} /></label></div><div className="membership-resign-modal-actions"><button type="button" className="membership-cancel-action" onClick={handleCloseResignModal}>Anuluj</button><button type="button" className="membership-resign-confirm-action" onClick={handleResign} disabled={!resignedAt || isSaving}>{isSaving ? "Zapisywanie..." : "Zakończ członkostwo"}</button></div></div></div>}
        </div>
    );
};

export default MembershipView;
